import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  IOpportunitySegment,
  NextSteps,
  OpportunityService,
} from '@techspert-io/opportunities';
import { PatternsModule } from '@techspert-io/patterns';
import {
  EMPTY,
  catchError,
  combineLatest,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-request-recruitment-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    PatternsModule,
  ],
  templateUrl: './request-recruitment-dialog.component.html',
  styleUrl: './request-recruitment-dialog.component.scss',
})
export class RequestRecruitmentDialogComponent implements OnInit {
  projectState:
    | 'submitting-project'
    | 'submitted-project'
    | 'failed-submission';

  detailsForm = new FormGroup({
    segments: new FormArray<
      FormGroup<{
        segmentId: FormControl<string>;
        recruitmentAmount: FormControl<number>;
      }>
    >(
      [
        new FormGroup({
          segmentId: new FormControl<string>('', Validators.required),
          recruitmentAmount: new FormControl<number>(0, [
            Validators.required,
            Validators.min(1),
          ]),
        }),
      ],
      [Validators.required, Validators.minLength(1)]
    ),
    recruitmentCriteria: new FormControl('', Validators.required),
    timelines: new FormControl('', Validators.required),
    files: new FormControl<File[]>([]),
    nextSteps: new FormControl<NextSteps[]>([], Validators.required),
  });

  submitted = false;

  get files() {
    return this.detailsForm.controls.files.value || [];
  }

  get segmentsArray() {
    return this.detailsForm.controls.segments;
  }

  constructor(
    private dialogRef: MatDialogRef<RequestRecruitmentDialogComponent>,
    private opportunityService: OpportunityService,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      opportunityId: string;
      segments: IOpportunitySegment[];
    }
  ) {}

  ngOnInit() {
    this.initializeFirstSegment();
  }

  getAvailableSegments(index: number) {
    const currentControl = this.segmentsArray.at(index);
    const currentId = currentControl.controls.segmentId.value;

    const selectedIds = this.segmentsArray.controls
      .map((ctrl, i) => (i !== index ? ctrl.controls.segmentId.value : null))
      .filter(Boolean);

    return this.data.segments.filter(
      (seg) =>
        seg.opportunitySegmentId === currentId ||
        !selectedIds.includes(seg.opportunitySegmentId)
    );
  }

  addSegment() {
    const available = this.data.segments.filter(
      (s) =>
        !this.segmentsArray.controls.some(
          (ctrl) => ctrl.controls.segmentId.value === s.opportunitySegmentId
        )
    );

    const noAvailableSegments =
      this.segmentsArray.length >= this.data.segments.length;

    if (!available.length || noAvailableSegments) {
      return;
    }

    const [segment, ...rest] = available;

    const prefillId =
      segment && !rest.length ? segment.opportunitySegmentId : '';

    this.segmentsArray.push(
      new FormGroup({
        segmentId: new FormControl<string>(prefillId, Validators.required),
        recruitmentAmount: new FormControl<number>(0, [
          Validators.required,
          Validators.min(1),
        ]),
      })
    );
  }

  addAllSegments() {
    this.segmentsArray.clear();

    this.data.segments.forEach((s) => {
      this.segmentsArray.push(
        new FormGroup({
          segmentId: new FormControl<string>(
            s.opportunitySegmentId,
            Validators.required
          ),
          recruitmentAmount: new FormControl<number>(0, [
            Validators.required,
            Validators.min(1),
          ]),
        })
      );
    });
  }

  removeSegment(index: number) {
    this.segmentsArray.removeAt(index);
  }

  onFilesAdd(target: HTMLInputElement) {
    const files = Array.from(target?.files || []);
    const currentFiles = this.files || [];
    this.detailsForm.patchValue({
      files: [...currentFiles, ...files],
    });
  }

  removeFile(index: number) {
    const currentFiles = this.files || [];
    currentFiles.splice(index, 1);
    this.detailsForm.patchValue({
      files: currentFiles,
    });
  }

  closeDialog(success: boolean) {
    this.dialogRef.close(success);
  }

  submit() {
    this.submitted = true;

    if (this.detailsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      return;
    }

    this.projectState = 'submitting-project';
    const { segments, recruitmentCriteria, timelines, files, nextSteps } =
      this.detailsForm.value;

    const fileProcessing = files.map((f) =>
      this.opportunityService
        .uploadOpportunityRfqFile(f.name)
        .pipe(
          switchMap((res) =>
            this.opportunityService
              .uploadFile(f, res.uploadUrl)
              .pipe(map(() => res.fileKey))
          )
        )
    );

    (files.length ? combineLatest(fileProcessing) : of([]))
      .pipe(
        switchMap((fileUploadUrls) =>
          this.opportunityService
            .requestRecruitment({
              opportunityId: this.data.opportunityId,
              segments: segments.map((s) => ({
                segmentId: s.segmentId,
                recruitmentAmount: s.recruitmentAmount,
              })),
              recruitmentCriteria,
              timelines,
              nextSteps,
              fileUploadUrls,
            })
            .pipe(
              tap(() => {
                this.projectState = 'submitted-project';
              }),
              catchError(() => {
                this.projectState = 'failed-submission';
                return EMPTY;
              })
            )
        )
      )
      .subscribe();
  }

  private initializeFirstSegment() {
    const segmentsArray = this.segmentsArray;

    if (segmentsArray.length === 1) {
      const available = this.getAvailableSegments(0);

      if (available.length === 1) {
        segmentsArray
          .at(0)
          .controls.segmentId.setValue(available[0].opportunitySegmentId);
      }
    }
  }
}
